import React, {
  ReactNode,
  useState,
  createContext,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { useLocation } from 'react-router';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Link from '@mui/material/Link';

export type AlertType = {
  actions?: [{ onClick: () => void; title: string }];
  text: string | React.ReactNode;
  type: 'success' | 'error' | 'info' | 'warning';
};

export type AlertDispatcher = (alert?: AlertType | null) => void;

export const AlertContext = createContext<AlertDispatcher | undefined>(
  undefined
);

export default function AlertProvider(
  props: Readonly<{ children: ReactNode }>
) {
  const [alert, setAlert] = useState<AlertType | null>();
  const [alertOpen, setAlertOpen] = useState(false);
  const alertRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const showAlert = useCallback((alert) => {
    if (alert) {
      alertRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
      setAlert(alert);
    }
    setAlertOpen(Boolean(alert));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => showAlert(undefined), [location.pathname]);

  return (
    <>
      <Collapse
        in={alertOpen}
        onExited={() => setAlert(undefined)}
        ref={alertRef}
      >
        <Alert
          data-testid="alert-box"
          iconMapping={{ success: <CheckCircleOutlineIcon /> }}
          onClose={() => setAlertOpen(false)}
          severity={alert?.type ?? 'success'}
          sx={{
            '& .MuiAlert-action': { p: 0 },
            maxWidth: '720px',
            mx: 'auto',
            width: 'fit-content',
          }}
        >
          {alert?.text ?? ''}
          {alert?.actions ? ' ' : ''}
          {alert?.actions?.map((a) => (
            <Link
              key={a.title}
              onClick={() => {
                showAlert(null);
                a.onClick();
              }}
              style={{ margin: '0 4px' }}
            >
              {a.title}
            </Link>
          ))}
        </Alert>
      </Collapse>

      <AlertContext.Provider value={showAlert}>
        {props.children}
      </AlertContext.Provider>
    </>
  );
}
